import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Save, Upload, Crown, ShieldCheck, Loader2 } from "lucide-react";

const initialForm = {
  full_name: "JAKIR MOMIN",
  designation: "प्रधान संपादक एवं मालिक · Chief Editor & Owner",
  id_number: "IN21-CE-001",
  photo_url: "",
  signature_url: "",
  dob: "",
  blood_group: "",
  address: "सिरसी नवापारा, मेन रोड, सूरजपुर, छत्तीसगढ़ — 497231",
  phone: "+91 8463860303",
  email: "mdjkbaba786@gmail.com",
  valid_till: "2030-12-31",
  issued_on: "",
  rni_no: "",
  tagline: "भारत की आवाज़",
};

export default function OwnerIDCardPage() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ photo: false, signature: false });
  const photoRef = useRef();
  const sigRef = useRef();

  useEffect(() => {
    api.get("/admin/owner-card")
      .then((r) => setForm({ ...initialForm, ...r.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file, field) => {
    const kind = field === "photo_url" ? "photo" : "signature";
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?watermark=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set(field, data.url);
      toast.success("अपलोड हो गया");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/owner-card", form);
      toast.success("सहेज दिया गया");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-slate-500">लोड हो रहा है…</div>;

  const qrData = JSON.stringify({
    org: "INDIAN-NEWS-21",
    role: "Chief Editor & Owner",
    name: form.full_name,
    id: form.id_number,
    valid: form.valid_till,
    site: "www.indian-news-21",
  });

  return (
    <div data-testid="owner-id-card-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3 no-print">
        <div>
          <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3">
            <Crown className="w-7 h-7 text-amber-500"/> प्रधान संपादक ID कार्ड
          </h1>
          <p className="text-sm text-slate-500 mt-1">Chief Editor & Owner का प्रीमियम पहचान पत्र · गोल्ड होलोग्राम एडिशन</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="bg-[#0F172A] hover:bg-[#1E3A8A]" data-testid="owner-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Save className="w-4 h-4 mr-2"/>}
            सहेजें
          </Button>
          <Button onClick={() => window.print()} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="owner-print-btn">
            <Printer className="w-4 h-4 mr-2"/> प्रिंट / PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
        {/* Form */}
        <div className="xl:col-span-2 bg-white rounded-lg border border-slate-200 p-6 space-y-3 no-print">
          <h3 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600"/>विवरण</h3>
          <Field label="पूरा नाम"><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="owner-name"/></Field>
          <Field label="पद"><Input value={form.designation} onChange={(e) => set("designation", e.target.value)} data-testid="owner-designation"/></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="ID संख्या"><Input value={form.id_number} onChange={(e) => set("id_number", e.target.value)} data-testid="owner-idno"/></Field>
            <Field label="RNI संख्या"><Input value={form.rni_no} onChange={(e) => set("rni_no", e.target.value)} placeholder="RNI/CHHHIN/…"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="जारी दिनांक"><Input type="date" value={form.issued_on} onChange={(e) => set("issued_on", e.target.value)}/></Field>
            <Field label="वैध तक"><Input type="date" value={form.valid_till} onChange={(e) => set("valid_till", e.target.value)} data-testid="owner-valid"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="जन्म तिथि"><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)}/></Field>
            <Field label="रक्त समूह"><Input value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} placeholder="O+"/></Field>
          </div>
          <Field label="फ़ोन"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)}/></Field>
          <Field label="ईमेल"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}/></Field>
          <Field label="पता"><Input value={form.address} onChange={(e) => set("address", e.target.value)}/></Field>
          <Field label="टैगलाइन"><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)}/></Field>

          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <UploadBox label="फोटो" url={form.photo_url} onPick={() => photoRef.current?.click()} loading={uploading.photo}/>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "photo_url")}/>
            <UploadBox label="हस्ताक्षर" url={form.signature_url} onPick={() => sigRef.current?.click()} loading={uploading.signature}/>
            <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "signature_url")}/>
          </div>
        </div>

        {/* Card preview */}
        <div className="xl:col-span-3">
          <div id="owner-card-print" className="mx-auto" style={{ maxWidth: 700 }} data-testid="owner-card-preview">
            <OwnerCard form={form} qrData={qrData}/>
          </div>
          <div className="text-xs text-slate-500 mt-4 max-w-md mx-auto no-print text-center">
            प्रिंट सुझाव: कार्ड आकार <b>3.5" × 5.5"</b> (Premium Landscape)। प्रिंट डायलॉग में "बैकग्राउंड ग्राफ़िक्स" ऑन रखें।
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: 5.5in 3.5in landscape; margin: 0.15in; }
          body { background: white !important; }
          .no-print { display: none !important; }
          #owner-card-print { max-width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-semibold mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function UploadBox({ label, url, onPick, loading }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-admin text-slate-500 font-semibold mb-1">{label}</div>
      <div onClick={onPick} className="cursor-pointer h-24 rounded-md border-2 border-dashed border-slate-300 hover:border-[#EA580C] flex items-center justify-center overflow-hidden bg-slate-50">
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500"/> : url ? (
          <img src={url} alt="" className="w-full h-full object-contain"/>
        ) : (
          <div className="text-xs text-slate-400 flex items-center gap-1"><Upload className="w-4 h-4"/>अपलोड करें</div>
        )}
      </div>
    </div>
  );
}

function OwnerCard({ form, qrData }) {
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl print:shadow-none" style={{
      aspectRatio: "5.5 / 3.5",
      background: "linear-gradient(135deg, #0F172A 0%, #1E293B 40%, #0F172A 100%)",
      color: "white",
      fontFamily: "'Times New Roman', serif",
    }}>
      {/* Hologram / guilloché pattern */}
      <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, rgba(251,191,36,.4) 0 1px, transparent 1px 6px),
          repeating-linear-gradient(-45deg, rgba(251,191,36,.25) 0 1px, transparent 1px 10px)`,
      }}/>
      {/* Gold ring corners */}
      <div className="absolute inset-2 rounded-xl border border-amber-400/40 pointer-events-none"/>
      <div className="absolute inset-3 rounded-xl border border-amber-400/20 pointer-events-none"/>
      {/* Watermark logo */}
      <div className="absolute right-6 bottom-6 opacity-[0.06] pointer-events-none">
        <img src="/logo.png" alt="" style={{ width: 220, height: 220 }} className="object-contain"/>
      </div>

      {/* Top masthead strip */}
      <div className="relative flex items-center gap-3 px-5 pt-4">
        <img src="/logo.png" alt="" className="w-11 h-11 rounded bg-white/95 object-contain shadow"/>
        <div className="flex-1 min-w-0">
          <div className="font-black text-white text-[18px] leading-none tracking-wider">INDIAN-NEWS-21</div>
          <div className="text-[9px] uppercase tracking-[0.28em] text-amber-300 mt-1 font-bold">{form.tagline || "भारत की आवाज़"} · Press · Media</div>
        </div>
        <div className="text-right">
          <div className="text-[8px] uppercase tracking-widest text-amber-300/80">Chief Editor</div>
          <div className="text-[10px] font-black text-amber-300">Owner Card</div>
        </div>
      </div>

      <div className="absolute top-[68px] left-5 right-5 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"/>

      {/* Body */}
      <div className="relative flex gap-4 px-5 pt-4 pb-4" style={{ marginTop: 4 }}>
        {/* Photo */}
        <div className="shrink-0" style={{ width: 112, height: 140 }}>
          <div className="w-full h-full rounded-lg overflow-hidden border-2 border-amber-400 bg-slate-800 shadow-lg">
            <img
              src={form.photo_url || "/jakir-momin-boss.png"}
              alt={form.full_name}
              className="w-full h-full object-cover"
              data-testid="owner-card-photo"
            />
          </div>
          <div className="text-[8px] uppercase tracking-widest text-amber-300/80 text-center mt-1">Verified · सत्यापित</div>
        </div>

        {/* Center info */}
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.24em] text-amber-300/90 font-bold">Name · नाम</div>
          <div className="text-white text-[22px] font-black leading-tight tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{form.full_name}</div>

          <div className="mt-1.5 text-[9px] uppercase tracking-[0.22em] text-amber-300/90 font-bold">Designation</div>
          <div className="text-white text-[11px] leading-tight font-semibold">{form.designation}</div>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2">
            <Info k="ID No." v={form.id_number}/>
            <Info k="Valid Till" v={form.valid_till}/>
            <Info k="Issued" v={form.issued_on}/>
            <Info k="RNI" v={form.rni_no || "—"}/>
            <Info k="Blood" v={form.blood_group || "—"}/>
            <Info k="Phone" v={form.phone}/>
          </div>
        </div>

        {/* QR + signature */}
        <div className="shrink-0 flex flex-col items-center gap-2" style={{ width: 92 }}>
          <div className="bg-white p-1 rounded">
            <QRCodeSVG value={qrData} size={78} level="H" bgColor="#ffffff" fgColor="#0F172A"/>
          </div>
          <div className="text-[7px] uppercase tracking-widest text-amber-300/80">Scan · स्कैन</div>
          <div className="mt-1 h-9 w-full flex items-end justify-center">
            {form.signature_url ? (
              <img src={form.signature_url} alt="sig" style={{ maxHeight: 36 }} className="object-contain"/>
            ) : (
              <div
                className="text-amber-300 text-[16px] italic font-bold leading-none"
                style={{ fontFamily: "'Times New Roman', serif" }}
              >J. Momin</div>
            )}
          </div>
          <div className="h-px w-full bg-amber-400/60"/>
          <div className="text-[7px] uppercase tracking-widest text-amber-300/80">Signature</div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-600 via-amber-400 to-amber-600 text-[#0F172A] px-4 py-1 flex items-center justify-between text-[9px] font-bold uppercase tracking-widest">
        <span>{form.address}</span>
        <span>ID: {form.id_number}</span>
      </div>
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div className="min-w-0">
      <div className="text-[8px] uppercase tracking-widest text-amber-300/70 font-bold">{k}</div>
      <div className="text-white text-[10.5px] font-mono truncate">{v || "—"}</div>
    </div>
  );
}
